import {
    createLogPrefix,
    logDebug,
    logWarn,
    type LogTarget,
    ReportableError,
    RequestError,
    type Maybe,
} from '@amazon/vinyl-util'
import type { CacheEntryOptions, CacheManager } from '@amazon/vinyl-cache-manager'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import type { GraphQLFormattedError } from 'graphql/error'
import { print } from 'graphql/language'
import { sanitizeGql, type SanitizedGql } from './gqlUtil'
import { saveResponseEntities } from './normalizedCache'

// Matches the app's historical `ctx('gql')` prefix so log output is unchanged.
const target: LogTarget = { logPrefix: createLogPrefix('gql') }

/**
 * Application-independent GQL fetch pipeline: APQ persisted-query round-trip,
 * retry, SWR caching through a provided {@link CacheManager}, error surfacing,
 * and node/edges sanitize on the way out. Everything application-specific —
 * auth, device identity, api keys, CSRF headers, origin, feature flags, the
 * idle-queue gate, perf instrumentation — is supplied by the app through
 * {@link GqlRequestContext}, so this module depends on no app code and can live
 * in a leaf package.
 */

export type GqlFetchOptions<Result = unknown> = {
    /**
     * If true, the response is cached to persistence under a key derived from
     * the operation name, persisted-query sha, and a hash of the variables and
     * any per-request `headers` (see {@link deriveGqlCacheKey}). Invalidate a
     * cached read later by passing the same query + variables + headers to
     * {@link invalidateGqlCache}.
     */
    readonly useCache?: boolean

    /**
     * If set, will retry once if the error is retryable.
     */
    readonly isRetryableError?: (error: GraphQLFormattedError) => boolean

    readonly cacheControlOverrides?: CacheEntryOptions['cacheControlOverrides']

    /**
     * If true, when the gql response contains errors, the fetch request will be rejected instead
     * of returning partial data.
     */
    readonly rejectPartialResponse?: boolean

    /**
     * Consulted before a `rejectPartialResponse` rejection: when it reports
     * true against the (unsanitized) response data, the sibling errors are
     * treated as non-fatal and the data is returned instead of thrown. Lets a
     * caller whose payload carries its own success signal (e.g. a mutation's
     * `status` field) avoid a genuine success being masked by an unrelated
     * partial error.
     */
    readonly isPartialSuccess?: (data: Maybe<Result>) => boolean

    /**
     * A LABEL override for the cache key, perf span and error messages — lets a
     * caller distinguish multiple calls that share one query shape (e.g. a paged
     * fetch named per window like `playlistDetail_50`). NOT sent on the wire: the
     * request always carries the query document's real operation name (from
     * {@link operationNameOf}), else the gateway rejects it as unknown.
     */
    readonly label?: string

    /**
     * If true, every {@link CacheableEntity} in the response (an object with
     * `__typename` + `id`) is saved to the normalized cache and REPLACED in the
     * returned response by the canonical cached instance. This makes the
     * response fly-weight: repeated occurrences of the same entity collapse to a
     * single shared reference and the app holds no duplicate of an entity it has
     * already cached. Default false so the plain `runGqlFetch` pipeline is
     * unchanged; the app's `gqlFetch` wrapper opts in.
     */
    readonly saveToNormalizedCache?: boolean

    /**
     * Extra request headers for this call. Added to the app's default headers,
     * which win on a name clash so a caller can never replace the wire contract.
     * Part of the cache key, so calls differing only in headers never share a
     * cached response.
     */
    readonly headers?: Readonly<Record<string, string>>
}

/**
 * The application-specific request shape for a single fetch, built by the app
 * from the current auth/device state. `headers` already includes api keys,
 * CSRF, feature flags, and device ids; `credentials` is omitted entirely when
 * the auth mode wants the same-origin default (never send `undefined`).
 */
export interface GqlRequestInit {
    readonly headers: Record<string, string>
    readonly credentials?: RequestCredentials
}

/**
 * The separately-timed phases of one fetch: `auth` is the request-init wait
 * (bearer refresh, cookie-session boot config, api key), `fetch` is the
 * cache-or-network read.
 */
export type GqlFetchPhase = 'auth' | 'fetch'

/** Where a `fetch` phase's response came from. */
export type GqlFetchSource = 'cache' | 'network'

/**
 * App-provided seam. The generic pipeline never reaches into app modules; the
 * app hands it these capabilities per call.
 */
export interface GqlRequestContext {
    /** SWR cache used for the cache-first strategy. */
    readonly cacheManager: CacheManager
    /** The gql endpoint URL (live-host / proxy resolution is the app's concern). */
    readonly origin: string
    /**
     * Builds the outgoing request headers + credentials from the current auth
     * and device state (api keys, CSRF triple, bearer envelope, device ids).
     * Called once per fetch, before the APQ retry loop, so the bearer token is
     * fetched a single time and both attempts share the same auth.
     */
    readonly buildRequestInit: () => Promise<GqlRequestInit>
    /**
     * Optional perf seam: opens a span for one {@link GqlFetchPhase} and returns
     * its one-shot `done`, told where a `fetch` phase's response came from. The
     * app draws the bars; the pipeline only reports the phase boundaries, so an
     * auth wait is never mistaken for fetch latency and a cached read is never
     * mistaken for a network round trip.
     */
    readonly beginPhase?: (phase: GqlFetchPhase) => (source?: GqlFetchSource) => void
}

const MAX_ATTEMPTS = 2

/**
 * Extracts the operation name from the query.
 */
const operationRegex = /(?:query|mutation|subscription)\s+([A-Za-z]+)/

export function operationNameOf(queryString: string): string | undefined {
    const [, operationName] = queryString.match(operationRegex) ?? []
    return operationName
}

/**
 * Bump this to make every customer skip cached gql data: it's mixed into every
 * derived cache key, so incrementing it changes all keys at once and the next
 * read misses (then re-caches under the new key). Orphaned pre-bump entries are
 * cleaned up by the CacheManager's staleness eviction — no explicit purge.
 */
const CACHE_BUST = '1'

async function sha256(input: string): Promise<string> {
    const data = new TextEncoder().encode(input)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    return [...new Uint8Array(hashBuffer)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

// Serialize with object keys recursively sorted so semantically-identical
// variables hash to one key regardless of key insertion order (array order is
// semantic and preserved). Without this, `{a,b}` and `{b,a}` miss each other's
// cache entry.
function stableStringify(value: unknown): string {
    return JSON.stringify(value, (_key, val) => {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            return Object.fromEntries(
                Object.keys(val)
                    .sort()
                    .map((k) => [k, (val as Record<string, unknown>)[k]])
            )
        }
        return val
    })
}

// Single source of truth for the derived key so the fetch-time derivation and
// the invalidation-time derivation can never drift. `querySha` is the
// persisted-query sha256 of the printed query (already computed on the fetch
// path, so it's threaded in rather than recomputed).
async function cacheKeyFromParts(
    operationName: string | undefined,
    querySha: string,
    variables: unknown,
    headers: Readonly<Record<string, string>> | undefined
): Promise<string> {
    const variablesHash = await sha256(stableStringify(variables))
    const headersPart = headers ? `:${await sha256(stableStringify(headers))}` : ''
    return `${operationName ?? 'anonymous'}:${querySha}:${variablesHash}${headersPart}:${CACHE_BUST}`
}

/**
 * Derive the cache key for a `gqlFetch({ useCache: true })` call. Prefixed with
 * the operation name so cache entries are identifiable when inspecting storage,
 * then the persisted-query sha, a hash of the variables, a hash of any
 * per-request headers, and {@link CACHE_BUST}. Callers use this to invalidate a
 * cached read by deriving the same key from the query + variables + headers they
 * cached (see {@link invalidateGqlCache}).
 *
 * The CacheManager prepends {@link CACHE_KEY_PREFIX} on top of this when storing.
 */
export async function deriveGqlCacheKey<Result, Variables>(
    query: TypedDocumentNode<Result, Variables>,
    variables?: Variables,
    headers?: GqlFetchOptions['headers']
): Promise<string> {
    const queryString = print(query)
    const querySha = await sha256(queryString)
    return cacheKeyFromParts(operationNameOf(queryString), querySha, variables, headers)
}

function mergeHeaders(
    defaults: Readonly<Record<string, string>>,
    extra: Readonly<Record<string, string>> | undefined
): Headers {
    const merged = new Headers(extra)
    for (const [name, value] of Object.entries(defaults)) merged.set(name, value)
    return merged
}

/**
 * Post-process a sanitized result before it leaves the pipeline: when
 * `saveToNormalizedCache` is set, save every cacheable entity and flyweight the
 * response against the cache. Otherwise the result passes through untouched.
 */
function finalize<Result>(
    result: SanitizedGql<Result>,
    fetchOptions: GqlFetchOptions<Result> | undefined
): SanitizedGql<Result> {
    return fetchOptions?.saveToNormalizedCache ? saveResponseEntities(result) : result
}

/**
 * Run the application-independent fetch pipeline. The app's `gqlFetch` builds
 * the {@link GqlRequestContext} from live auth/device state and delegates here.
 * `options.apqDisabled` and `options.noCache` are debug knobs the app resolves
 * and forwards.
 */
export async function runGqlFetch<Result, Variables>(
    ctx: GqlRequestContext,
    query: TypedDocumentNode<Result, Variables>,
    variables: Variables | undefined,
    fetchOptions: GqlFetchOptions<Result> | undefined,
    options: { readonly apqDisabled: boolean; readonly noCache: boolean }
): Promise<SanitizedGql<Result>> {
    const queryString = print(query)
    // The request body MUST carry the operation name defined in the query
    // document — the gateway rejects any other value as an unknown operation.
    // The optional override only labels the cache key, perf span and error
    // messages (e.g. `playlistDetail_50` per page); it never goes on the wire.
    const wireOperationName = operationNameOf(queryString)
    const label = fetchOptions?.label ?? wireOperationName
    if (!wireOperationName) logWarn(target, 'Could not infer operation name', query)

    // The persisted-query sha and the derived cache key drive the SWR cache
    // below, so compute them up front (the sha doubles as the APQ `sha256Hash`
    // further down).
    const querySha = await sha256(queryString)
    const cacheKey = await cacheKeyFromParts(label, querySha, variables, fetchOptions?.headers)

    const extensions = options.apqDisabled
        ? undefined
        : {
              persistedQuery: {
                  version: 1,
                  sha256Hash: querySha,
              },
          }

    // `?noCache=true` forces every gql request network-only: leaving the
    // effective key null makes the CacheManager skip both the read and the
    // write (see the cache-first strategy below), so the app bypasses the
    // SWR cache app-wide for the debugging session. Caching is opt-in per
    // call via `useCache`; the key is always the auto-derived one.
    const effectiveCacheKey: string | null =
        fetchOptions?.useCache && !options.noCache ? cacheKey : null

    // Built once before the retry loop so a token-auth bearer is fetched a
    // single time and both APQ attempts present identical auth. Reported as the
    // `auth` phase so an app profiler can split the auth wait off the fetch
    // bar — on AuthPortal this covers the `/config.json` boot wait, which would
    // otherwise be counted as fetch latency even for a cache hit.
    const authDone = ctx.beginPhase?.('auth')
    const { headers: defaultHeaders, credentials } = await ctx.buildRequestInit()
    authDone?.()
    const headers = mergeHeaders(defaultHeaders, fetchOptions?.headers)

    let attempts = 0
    // With APQ disabled, the first attempt sends the full query body — there's
    // no hash-only round-trip to retry from.
    let includeQuery = options.apqDisabled

    while (++attempts <= MAX_ATTEMPTS) {
        // First attempt: hash only (Firefly recognises common queries by hash,
        // saves request size). On a PersistedQueryNotFound, retry with the full
        // query body included alongside the hash so Firefly's APQ layer can
        // register the new query for future requests.
        const body: Record<string, unknown> = {
            variables,
            operationName: wireOperationName,
            ...(extensions ? { extensions } : null),
            ...(includeQuery ? { query: queryString } : null),
        }

        let response: Response
        const fetchDone = ctx.beginPhase?.('fetch')
        try {
            // With a cacheKey set (`useCache`), use a cache-first strategy.
            response = await ctx.cacheManager.get(
                new Request(ctx.origin, {
                    headers,
                    body: JSON.stringify(body),
                    method: 'POST',
                    ...(credentials ? { credentials } : null),
                }),
                {
                    cacheKey: effectiveCacheKey,
                    cacheControlOverrides: {
                        // With a cacheKey set, override the GQL cache headers to allow
                        // response caching. With no cacheKey (no `useCache`), this has no
                        // effect and requests are network-only.
                        noStore: false,
                        maxAge: 0,
                        staleWhileRevalidate: 7 * 24 * 60 * 60,
                        ...fetchOptions?.cacheControlOverrides,
                    },
                    responseIsValid: async (response) => {
                        // Do not cache if there are errors, prefer stale cached responses if there are
                        // errors in the revalidated response.
                        const result = await response.clone().json()
                        return !result.errors
                    },
                }
            )
            // CacheManager stamps `X-Cached-Timestamp` only when `put` wrote the
            // response into storage, so a cached read carries it and a raw
            // network response does not — reliable source signal.
            fetchDone?.(response.headers.has(CACHED_TIMESTAMP_HEADER) ? 'cache' : 'network')
        } catch (error) {
            fetchDone?.('network')
            if (error instanceof RequestError) {
                if (error.response) {
                    const errorJson = await error.response.json().catch(() => ({}))
                    // Preserve the real HTTP status: it's the only signal that
                    // distinguishes a genuine transport-level auth rejection (401,
                    // credentials themselves invalid) from a resolver-level business
                    // denial (anon/tier content gating), which arrives as a 200 OK
                    // response with a `result.errors` body instead and never reaches
                    // this catch — see `isSessionExpiredGqlError`.
                    // A non-GraphQL gateway body (a 502 `{"message":…}`, an HTML page) has no
                    // `errors` array; without the fallback the constructor's own
                    // printGqlErrors would throw here and the caller would never
                    // see a GqlError at all.
                    throw new GqlError(
                        errorJson.errors ?? [],
                        label,
                        variables,
                        error.response.status
                    )
                } else throw error
            } else {
                throw error
            }
        }
        const result = await response.json()
        if (result.errors) {
            const persistErr = result.errors.find(
                (e: GqlError) => e.message === 'PersistedQueryNotFound'
            )
            // Expected APQ cold-miss: the hash-only first attempt 404s an
            // unregistered query, and we immediately resend the full body so
            // Firefly registers it for next time. That self-recovering round-trip
            // is normal per new query hash — log it at debug, not warn. A
            // PersistedQueryNotFound that survives the full-query resend
            // (`includeQuery` already true) is a genuine failure and falls through
            // to the loud warn below.
            if (persistErr && !includeQuery) {
                logDebug(target, printGqlErrors(result.errors))
                includeQuery = true
                continue
            }

            logWarn(target, label ?? 'anonymous', printGqlErrors(result.errors))

            const shouldRetry: boolean =
                attempts < MAX_ATTEMPTS &&
                !!fetchOptions?.isRetryableError &&
                result.errors.find((error: GraphQLFormattedError) =>
                    fetchOptions.isRetryableError!(error)
                )

            if (shouldRetry) logDebug(target, 'retrying', label)
            if (!shouldRetry) {
                // Check the caller's own success signal (if any) before
                // rejecting: a genuine success carrying an unrelated partial
                // error must not be thrown away.
                const isPartialSuccess = fetchOptions?.isPartialSuccess?.(result.data)
                if (fetchOptions?.rejectPartialResponse && !isPartialSuccess) {
                    throw new GqlError(result.errors, label, variables)
                }
                // Not a retryable error, return the data that could be resolved.
                return finalize(sanitizeGql(result.data), fetchOptions)
            }
        } else {
            return finalize(sanitizeGql(result.data), fetchOptions)
        }
    }
    throw new GqlError([], label, variables)
}

export class GqlError extends ReportableError {
    get [Symbol.toStringTag](): string {
        return 'GqlError'
    }

    constructor(
        /**
         * Rows straight off the wire, so this is the FORMATTED error shape —
         * `extensions` is optional and must be read with `?.`, unlike
         * graphql-js's `GraphQLError` class where it is always present.
         */
        readonly errors: readonly GraphQLFormattedError[],
        readonly operationName?: string,
        readonly variables?: unknown,
        /**
         * The real transport HTTP status, set only when this GqlError was
         * converted from a non-2xx `RequestError` response (a genuine
         * transport-level rejection). A resolver-level `result.errors` body
         * (200 OK) never sets this.
         */
        readonly httpStatus?: number
    ) {
        // Variable VALUES can carry tokens/PII, so only the shallow key NAMES go
        // into the log string; the full `variables` stays a structured field for
        // the error handler to attach when it needs the payload.
        super(
            `A service error occurred in ${operationName ?? 'anonymous'}` +
                `${variableKeyNames(variables)}: ${printGqlErrors(errors)}`
        )
        Object.setPrototypeOf(this, GqlError.prototype)
    }
}

function printGqlErrors(errors: readonly GraphQLFormattedError[]): string {
    return 'gql errors: ' + errors.map((error) => error.message).join('\n')
}

function variableKeyNames(variables: unknown): string {
    if (!variables || typeof variables !== 'object') return ''
    const keys = Object.keys(variables)
    return keys.length ? ` (variables: ${keys.join(', ')})` : ''
}

// CacheManager prefixes every cacheKey with this when storing a cache entry,
// so to invalidate a key we have to prefix it the same way.
const CACHE_KEY_PREFIX = 'https://cache/'

// Response header CacheManager stamps on every entry it writes; a bare network
// response doesn't carry it. The pipeline reads this to label a `fetch` phase
// as a cache hit vs a network round trip (see {@link GqlFetchSource}).
const CACHED_TIMESTAMP_HEADER = 'X-Cached-Timestamp'

/**
 * Invalidate the cached response for a `gqlFetch({ useCache: true })` call. Pass
 * the SAME query + variables + headers the read was made with — the key is re-derived via
 * {@link deriveGqlCacheKey}, so it targets the exact entry that read cached
 * under. Use this after a mutation to ensure the next read returns fresh data
 * instead of the stale cached payload.
 */
export async function invalidateGqlCache<Result, Variables>(
    cacheManager: CacheManager,
    query: TypedDocumentNode<Result, Variables>,
    variables?: Variables,
    headers?: GqlFetchOptions['headers']
): Promise<void> {
    const cacheKey = await deriveGqlCacheKey(query, variables, headers)
    await cacheManager.delete(CACHE_KEY_PREFIX + cacheKey)
}
